import React, {useState, useEffect} from "react"
import {sortBy} from "lodash"
import Select, {SelectOption} from "@irati/ui/src/FormItem/Select"
import SelectOptionComponent from "@irati/ui/src/FormItem/SelectOption"
import {BillingRegion, Region, RegionActiveState} from "@irati/types"
import buService from "@irati/billing/services/bu"
import regionService from "@irati/region/services/regionData"
import idHelper from "@irati/utils/id"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import adminTracking from "@irati-tracking/admin"

import AddNewCountryOption from "./AddNewCountyOption"
import CountriesNotOnListOption from "./CountriesNotOnListOption"
import DisabledHeaderOption from "./DisabledHeaderOption"

export type BURegionOnChange = (value: BillingRegion) => void

const ON_COUNTRIES_LIST = "platform.BUregion.onCountriesList"
const NOT_ON_COUNTRIES_LIST = "platform.BUregion.notOnCountriesList"
const ADD_NEW_COUNTRY_OPTION = "platform.BUregion.addNewCountry"

interface Props extends InjectIntl {
  id?: string
  businessId: string
  value: BillingRegion
  onChange: BURegionOnChange
  /** @description: Filter out units already on the business, defaults to true */
  filterExisting: boolean
  /** @description: Empty state text, defaults to "All available units added" */
  noOptionsMessage?: string
  label?: string
  /** @description: Separates exisiting business units from non-existing whilst displaying both inside the select */
  separateExisting?: boolean
  hideLabel?: boolean
  source?: string
}

const BURegion: React.FC<Props> = (props) => {
  const select = React.useRef()
  const filterExisting = props.filterExisting ?? true
  const [isLoading, setLoading] = useState(true)
  const [options, setOptions] = useState<SelectOption[]>([])

  const search = (option: SelectOption, searchTerm: string) => {
    if (option.value === ON_COUNTRIES_LIST || option.value === NOT_ON_COUNTRIES_LIST) {
      return true
    }
    if (searchTerm) {
      return !!option.label.toLowerCase().includes(searchTerm.toLowerCase())
    }
    return true
  }

  const buildOption = (optionProps) => {
    if (optionProps.value === ADD_NEW_COUNTRY_OPTION) {
      return <AddNewCountryOption {...optionProps} />
    }
    if (optionProps.value === ON_COUNTRIES_LIST || optionProps.value === NOT_ON_COUNTRIES_LIST) {
      return <DisabledHeaderOption {...optionProps} />
    }
    if (optionProps.data?.isCountryNew) {
      return <CountriesNotOnListOption {...optionProps} />
    }
    return <SelectOptionComponent {...optionProps} />
  }

  const updateOptionsToShowNewCountries = async () => {
    const regions = await regionService.getAllRegions()
    let opts = sortBy(
      regions
        .filter(({id, state}) => id !== BillingRegion.Unknown && id !== BillingRegion.All && state === RegionActiveState.Active)
        .map((region: Region) => {
          const {name, id} = region
          return {value: id, label: name}
        }),
      "label"
    )

    const billingUnits = await buService.list({businessId: props.businessId}, {fetchPolicy: "network-only"})
    const hasUnits = billingUnits.map((unit) => unit.regionId)
    const existingUnits = opts.filter((t) => hasUnits.includes(t.value))
    const newUnits = opts.filter((t) => !hasUnits.includes(t.value)).map((t) => ({...t, isCountryNew: true}))
    const existingHeader = {
      value: ON_COUNTRIES_LIST,
      label: props.formatMessage({id: ON_COUNTRIES_LIST}),
      isDisabled: true
    }
    const newHeader = {
      value: NOT_ON_COUNTRIES_LIST,
      label: props.formatMessage({id: NOT_ON_COUNTRIES_LIST}),
      isDisabled: true
    }

    opts = [existingHeader, ...existingUnits, newHeader, ...newUnits]

    setOptions(opts)
    setLoading(false)

    if (select.current) {
      select.current.onMenuOpen()
    }
  }

  const handleChange = (value, option) => {
    if (option.value === ADD_NEW_COUNTRY_OPTION) {
      adminTracking.adminPeopleAddCountry(props.source)
      updateOptionsToShowNewCountries()
      return props.onChange(null, option)
    }
    return props.onChange(value, option)
  }

  useEffect(() => {
    ;(async () => {
      const regions = await regionService.getAllRegions()
      let opts = sortBy(
        regions
          .filter(({id, state}) => id !== BillingRegion.Unknown && id !== BillingRegion.All && state === RegionActiveState.Active)
          .map((region: Region) => {
            const {name, id} = region
            return {value: id, label: name}
          }),
        "label"
      )

      if (props.separateExisting) {
        const billingUnits = await buService.list({businessId: props.businessId}, {fetchPolicy: "network-only"})
        const hasUnits = billingUnits.map((unit) => unit.regionId)
        const existingUnits = opts.filter((t) => hasUnits.includes(t.value))
        const existingHeader = {
          value: ON_COUNTRIES_LIST,
          label: props.formatMessage({id: ON_COUNTRIES_LIST}),
          isDisabled: true
        }

        opts = [
          existingHeader,
          ...existingUnits,
          {value: ADD_NEW_COUNTRY_OPTION, label: props.formatMessage({id: ADD_NEW_COUNTRY_OPTION})}
        ]
      } else if (filterExisting) {
        const billingUnits = await buService.list({businessId: props.businessId}, {fetchPolicy: "network-only"})
        const hasUnits = billingUnits.map((unit) => unit.regionId)
        opts = opts.filter((t) => !hasUnits.includes(t.value))
      }

      setOptions(opts)
      setLoading(false)
    })()
  }, [])

  return (
    <Select
      selectRef={select}
      components={{
        Option: buildOption
      }}
      id={idHelper("billing-unit", props)}
      label={props.label || "platform.BUregion.country"}
      options={options}
      noOptionsMessage={() => props.noOptionsMessage ?? "platform.BUregion.noCountriesAvailable"}
      value={props.value}
      onChange={handleChange}
      hideLabel={props.hideLabel}
      isLoading={isLoading}
      optionRenderer={buildOption}
      filterOption={search}
    />
  )
}

export default injectIntl(BURegion)
